import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { usePwaStore } from "@/lib/pwa/pwa-store";
import { usePushNotifications } from "@/lib/pwa/use-push-notifications";
import { usePwaInstall } from "@/lib/pwa/use-pwa-install";
import { useRouteContext } from "@tanstack/react-router";
import { Bell, Download, PlusSquare, RefreshCw, Share } from "lucide-react";
import { useEffect, useState } from "react";

export function PwaHeaderActions() {
  const [mounted, setMounted] = useState(false);
  const { user } = useRouteContext({ from: "/_app" });
  const { isInstallable, isIos, install } = usePwaInstall();
  const {
    permission,
    isSubscribed,
    subscribe,
    isLoading: isPushLoading,
  } = usePushNotifications();

  const {
    updateAvailable,
    setUpdateAvailable,
    isInstallDismissed,
    dismissInstall,
    isNotifDismissed,
    dismissNotif,
  } = usePwaStore();

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || !user) return null;

  const showUpdate = updateAvailable;
  const showInstall = isInstallable;
  const showNotif =
    !isPushLoading &&
    permission === "default" &&
    !isSubscribed &&
    typeof Notification !== "undefined";

  const showInstallDot = showInstall && !isInstallDismissed();
  const showNotifDot = showNotif && !isNotifDismissed();

  if (!showUpdate && !showInstall && !showNotif) return null;

  return (
    <div className="flex items-center gap-0.5">
      {/* Update available */}
      {showUpdate && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            setUpdateAvailable(false);
            window.location.reload();
          }}
          className="relative h-8 w-8 text-blue-500 hover:text-blue-400"
          aria-label="Güncelleme mevcut — yenile"
        >
          <RefreshCw className="h-4 w-4 animate-spin-slow" />
        </Button>
      )}

      {/* Install app */}
      {showInstall && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative h-8 w-8 text-muted-foreground hover:text-foreground"
              aria-label="Uygulamayı yükle"
            >
              <Download className="h-4 w-4" />
              {showInstallDot && (
                <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-primary" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72" align="end" sideOffset={8}>
            {isIos ? (
              <div className="space-y-3">
                <div>
                  <p className="font-semibold text-sm">Ana Ekrana Ekle</p>
                  <p className="mt-0.5 text-muted-foreground text-xs">
                    Safari'de bu adımları izle:
                  </p>
                </div>
                <ol className="space-y-2.5">
                  <li className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted font-semibold text-[10px] text-muted-foreground">
                      1
                    </span>
                    <span className="text-xs leading-relaxed">
                      Alttaki{" "}
                      <Share className="-mt-0.5 mx-0.5 inline h-3.5 w-3.5" />
                      <strong>Paylaş</strong> butonuna bas
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted font-semibold text-[10px] text-muted-foreground">
                      2
                    </span>
                    <span className="text-xs leading-relaxed">
                      <PlusSquare className="-mt-0.5 mx-0.5 inline h-3.5 w-3.5" />
                      <strong>"Ana Ekrana Ekle"</strong> seçeneğini seç
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted font-semibold text-[10px] text-muted-foreground">
                      3
                    </span>
                    <span className="text-xs leading-relaxed">
                      Sağ üstten <strong>"Ekle"</strong> ye dokunarak onayla
                    </span>
                  </li>
                </ol>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={dismissInstall}
                >
                  Anladım
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="font-semibold text-sm">Uygulamayı Yükle</p>
                  <p className="mt-0.5 text-muted-foreground text-xs">
                    Ana ekrana ekleyerek hızlı erişim sağla.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1" onClick={install}>
                    Yükle
                  </Button>
                  <Button size="sm" variant="outline" onClick={dismissInstall}>
                    Kapat
                  </Button>
                </div>
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}

      {/* Push notifications */}
      {showNotif && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative h-8 w-8 text-muted-foreground hover:text-foreground"
              aria-label="Bildirimleri etkinleştir"
            >
              <Bell className="h-4 w-4" />
              {showNotifDot && (
                <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-primary" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72" align="end" sideOffset={8}>
            <div className="space-y-3">
              <div>
                <p className="font-semibold text-sm">Bildirimler</p>
                <p className="mt-0.5 text-muted-foreground text-xs">
                  Yeni içerik ve güncellemelerden haberdar ol.
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1" onClick={subscribe}>
                  Etkinleştir
                </Button>
                <Button size="sm" variant="outline" onClick={dismissNotif}>
                  Kapat
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
